import { Bell, ExternalLink } from 'react-feather';
import { DocumentNode } from 'graphql';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Locale } from '__generated__/graphql';

import { GetSessionQuery, useSession } from '../Auth/SessionProvider';
import AtletaLogo from '../../common/Common/AtletaLogo';
import EditCurrentUserMutation from '../../participant/Common/EditCurrentUserMutation';
import LanguageSelector from '../../common/Common/LanguageSelector';
import MarkChangelogReadMutation from './MarkChangelogReadMutation';
import NavBar from './NavBar';
import TopBar from './TopBar';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';

export interface AppLayoutProps {
  path?: string;
  children?: ReactNode | any;
}

const AppLayout = ({ path, children }: AppLayoutProps) => {
  const { user } = useSession();

  if (!user) {
    return children;
  }

  return (
    <Wrapper>
      <TopBar />
      <NavBar />

      {children}

      <Footer path={path} />
    </Wrapper>
  );
};

export const Wrapper = styled(UI.FlexContainer)`
  ${({ theme }) => css`
    flex-direction: column;
    min-height: 100vh;
    align-items: space-between;

    @media ${theme.devices.md} {
      padding-top: ${theme.navBarHeight.md}px;
    }
  `}
`;

interface FooterProps {
  path?: string;
}

const Footer = ({ path }: FooterProps) => {
  const { t } = useTranslation();
  const { user } = useSession();

  const [editLocale] = useMutation(
    EditCurrentUserMutation,
  );

  const saveLocale = user ? (locale: Locale) => editLocale({
    variables: {
      input: {
        locale,
      },
    },
  }) : undefined;

  return (
    <FooterContainer>
      <UI.Section>
        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.Div sc={{ textAlign: ['center', 'left'] }}>
            <UI.A href="/" sc={{ inlineBlock: true, mt: [0, 1], mr: 2 }} title={t('common:atleta')}>
              <AtletaLogo symbolOnly height={16} symbolColor="gray.500" />
            </UI.A>
          </UI.Div>

          <UI.FlexContainer sc={{ gap: 0.5 }}>
            <ChangelogButton allowOpenModal={!path} />
            <LanguageSelector blank onChangeLocale={saveLocale} />
          </UI.FlexContainer>
        </UI.FlexContainer>
      </UI.Section>
    </FooterContainer>
  );
};

interface ChangelogButtonProps {
  allowOpenModal?: boolean;
}

const ChangelogButton = ({ allowOpenModal }: ChangelogButtonProps) => {
  const { t } = useTranslation();
  const { user, changelogEntries } = useSession();
  const { formatNumber } = useLocale();

  const unreadEntriesCount = changelogEntries.filter((entry) => (
    user.changelog_last_read_at === null || new Date(entry.created_at) > new Date(user.changelog_last_read_at)
  )).length;

  const [openModal, closeModal] = useModal(() => (
    <ChangelogModal onClose={closeModal} />
  ), []);

  useEffect(() => {
    if (allowOpenModal && unreadEntriesCount > 0) {
      openModal();
    }
  }, [allowOpenModal, unreadEntriesCount, openModal]);

  return (
    <UI.Button
      onClick={() => openModal()}
      sc={{ blank: true, size: 'sm' }}
    >
      <UI.Span sc={{ flex: { gap: 0.25 } }}>
        <UI.Span>
          <UI.Icon>
            <Bell />
          </UI.Icon>
          {' '}
          {t('updates')}
        </UI.Span>
        {unreadEntriesCount > 0 && (
          <UI.Badge
            sc={{ brand: 'error' }}
            style={{ minWidth: 20, height: 20, lineHeight: '20px' }}
          >
            {formatNumber(unreadEntriesCount)}
          </UI.Badge>
        )}
      </UI.Span>
    </UI.Button>
  );
};

interface ChangelogModalProps {
  onClose?: () => unknown;
  refetchQueries?: DocumentNode[];
}

const ChangelogModal = ({ onClose, refetchQueries = [GetSessionQuery] }: ChangelogModalProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  const { changelogEntries, user } = useSession();

  const [changelogEntriesRead, setChangelogEntriesRead] = useState(
    changelogEntries.map((entry) => user.changelog_last_read_at && entry.published_at <= user.changelog_last_read_at),
  );

  const [markChangelogRead] = useMutation(MarkChangelogReadMutation, {
    refetchQueries,
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    markChangelogRead();
  }, [changelogEntries, markChangelogRead]);

  return (
    <UI.Modal close={onClose}>
      <UI.GridContainer>
        <UI.H4>
          {t('latest_updates')}
        </UI.H4>

        <UI.GridContainer sc={{ gutter: 0.25 }}>
          {changelogEntries.map((entry, index) => (
            <Fragment key={entry.id}>
              <UI.HR />
              <UI.LinkBlock
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setChangelogEntriesRead((entries) => {
                  const newEntries = [...entries];
                  newEntries[index] = true;
                  return newEntries;
                })}
                sc={{ py: 2 }}
              >
                <UI.H5 sc={{ strong: !changelogEntriesRead[index] }}>
                  {entry.title}
                  {(!changelogEntriesRead[index]) && (
                    <UI.Span
                      sc={{ inlineBlock: true, background: 'error', ml: 1 }}
                      style={{
                        width: 8, height: 8, borderRadius: '100%', marginTop: -1, marginBottom: 1,
                      }}
                    />
                  )}
                </UI.H5>
                <UI.Div sc={{ small: true, color: 'gray.500' }}>
                  {formatDate(
                    parseDate(entry.published_at, { format: 'internal_date', timezone: 'UTC' }),
                    { format: 'display_date' },
                  )}
                </UI.Div>
              </UI.LinkBlock>
            </Fragment>
          ))}
          <UI.HR />
        </UI.GridContainer>

        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.AButton
            href="https://about.atleta.cc/updates"
            target="_blank"
            rel="noopener noreferrer"
            sc={{ brand: 'secondary' }}
          >
            {t('view_all_updates')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.AButton>
          <UI.Button onClick={onClose}>
            {t('close')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.GridContainer>
    </UI.Modal>
  );
};

const FooterContainer = styled(UI.Div)`
  ${({ theme }) => css`
    margin-top: auto;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    @media ${theme.devices.sm} {
      padding-bottom: ${theme.navBarHeight.sm + 6}px;
    }

    @media ${theme.devices.md} {
      padding-bottom: 0;
    }
  `}
`;

export default AppLayout;
